import { useMemo, useState } from "react";
import type { Food } from "@/mocks/foods";
import StarRating from "@/components/base/StarRating";

export default function OrderPanel({ food }: { food: Food }) {
  const [selected, setSelected] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    food.specs.forEach((s) => {
      init[s.label] = s.options[0].name;
    });
    return init;
  });
  const [qty, setQty] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  const unitPrice = useMemo(() => {
    let price = food.price;
    food.specs.forEach((s) => {
      const chosen = s.options.find((o) => o.name === selected[s.label]);
      if (chosen) price += chosen.price;
    });
    return price;
  }, [food, selected]);

  const addToCart = () => {
    setToast(`已将 ${qty} 份「${food.name}」加入购物车（演示）`);
    window.setTimeout(() => setToast(null), 2200);
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">{food.category}</span>
        {food.tags.map((t) => (
          <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
            {t}
          </span>
        ))}
      </div>

      <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
        {food.name}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{food.short}</p>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
        <StarRating rating={food.rating} />
        <span className="flex items-center gap-1">
          <i className="ri-shopping-bag-3-line" />
          月售 {food.sales}+
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-map-pin-line" />
          {food.distance} km
        </span>
      </div>

      <div className="mt-5 flex items-end gap-2 rounded-lg bg-primary-50 px-4 py-3">
        <span className="text-sm text-primary-700">¥</span>
        <span className="font-heading text-3xl font-extrabold text-primary-600">{unitPrice}</span>
        <span className="pb-1 text-xs text-foreground-500">起 · 含所选规格</span>
      </div>

      <div className="mt-6 flex flex-col gap-5">
        {food.specs.map((spec) => (
          <div key={spec.label}>
            <p className="mb-2 text-sm font-bold text-foreground-800">{spec.label}</p>
            <div className="flex flex-wrap gap-2">
              {spec.options.map((opt) => {
                const isActive = selected[spec.label] === opt.name;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    onClick={() => setSelected((prev) => ({ ...prev, [spec.label]: opt.name }))}
                    className={`rounded-md border px-4 py-2 text-sm transition ${
                      isActive
                        ? "border-primary-500 bg-primary-50 font-bold text-primary-700"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
                    }`}
                  >
                    {opt.name}
                    {opt.price > 0 && <span className="ml-1 text-xs text-primary-600">+¥{opt.price}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <div className="flex items-center rounded-full border border-background-300 bg-background-50">
          <button
            type="button"
            aria-label="减少数量"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="flex h-11 w-11 items-center justify-center text-foreground-700 transition hover:text-primary-600"
          >
            <i className="ri-subtract-line text-lg" />
          </button>
          <span className="w-10 text-center font-heading text-lg font-bold text-foreground-950">{qty}</span>
          <button
            type="button"
            aria-label="增加数量"
            onClick={() => setQty((q) => Math.min(20, q + 1))}
            className="flex h-11 w-11 items-center justify-center text-foreground-700 transition hover:text-primary-600"
          >
            <i className="ri-add-line text-lg" />
          </button>
        </div>
        <button
          type="button"
          onClick={addToCart}
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
        >
          <i className="ri-shopping-cart-2-line text-base" />
          加入购物车 · ¥{unitPrice * qty}
        </button>
      </div>
      <p className="mt-3 text-xs text-foreground-400">* 加入购物车为前端演示效果，暂未接入真实下单与支付</p>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] flex -translate-x-1/2 animate-fade-up items-center gap-2 rounded-full bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50">
          <i className="ri-checkbox-circle-fill text-accent-400" />
          {toast}
        </div>
      )}
    </div>
  );
}